import paper from 'paper';
import { scaleNorth, scaleSouth, scaleWest } from './bounding-box-utils';
import {
    scaleSouthEast,
    scaleSouthWest,
    scaleNorthEast,
    scaleNorthWest,
    scaleEast,
    moveObject
} from './bounding-box-utils';

enum ACTIVE_OBJECTS {
    TOP_LEFT_CORNER = 'TOP_LEFT_CORNER',
    TOP_RIGHT_CORNER = 'TOP_RIGHT_CORNER',
    BOTTOM_LEFT_CORNER = 'BOTTOM_LEFT_CORNER',
    BOTTOM_RIGHT_CORNER = 'BOTTOM_RIGHT_CORNER',
    TOP_SEGMENT = 'TOP_SEGMENT',
    RIGHT_SEGMENT = 'RIGHT_SEGMENT',
    LEFT_SEGMENT = 'LEFT_SEGMENT',
    BOTTOM_SEGMENT = 'BOTTOM_SEGMENT',
    OBJECT = 'OBJECT'
}

export class RegionOfInterest extends paper.Layer {
    // Neurala-Specific Properties
    private roiArea: paper.Path.Rectangle;
    private cover: paper.PathItem;
    private neuralaActivePiece: ACTIVE_OBJECTS | null = null;
    private isChangeInProgress = false;
    private originalPoint: paper.Point | null = null;
    private originalSize: paper.Size | null = null;
    private editMode = false;
    private lastObjectBoundsPoint: paper.Point | null = null;
    // End Neurala-Specific Properties

    constructor(originalPoint: paper.Point | null = null, originalSize: paper.Size | null = null) {
        super({});
        this.selected = false;
        this.strokeScaling = false;
        this.visible = true;
        this.originalPoint = originalPoint ? originalPoint.clone() : null;
        this.originalSize = originalSize ? originalSize.clone() : null;

        this.on('mousedown', (e: paper.MouseEvent) => this.onMouseKeyPressed(e));
        this.on('mousemove', (e: paper.MouseEvent) => this.onMouseMoveOverLayer(e));
        this.view.on('mouseup', (e: paper.MouseEvent) => this.onMouseKeyReleased());

        this.drawArea(this.originalPoint, this.originalSize);
    }

    public enableEditMode() {
        if (this.roiArea) {
            this.roiArea.selected = this.isDefined();
        }
        this.locked = false;
        this.editMode = true;
        this.visible = true;
    }

    public getBounds() {
        return this.roiArea && this.roiArea.bounds;
    }

    public disableEditMode() {
        this.project.deselectAll();
        this.locked = true;
        this.editMode = false;
        this.visible = this.isDefined();
    }

    public isDefined(): boolean {
        return this.roiArea && this.roiArea.area > 0;
    }

    public canDelete(): boolean {
        return this.originalSize !== null && this.originalSize.height > 0 && this.originalSize.width > 0;
    }

    public hasChanges(): boolean {
        return (
            this.roiArea &&
            this.roiArea.bounds &&
            this.originalPoint !== null &&
            this.originalSize !== null &&
            (!this.originalPoint.equals(this.roiArea.bounds.point) ||
                !this.originalSize.equals(this.roiArea.bounds.size))
        );
    }

    public revertChanges(): void {
        this.drawArea(this.originalPoint, this.originalSize);
    }

    public markAsSaved(): void {
        if (!this.roiArea) {
            return;
        }

        this.originalPoint = this.roiArea.bounds.point;
        this.originalSize = this.roiArea.bounds.size;
    }

    public drawArea(point: paper.Point | null = null, size: paper.Size | null = null): void {
        if (!this.project.layers || !this.project.layers['assetLayer']) {
            return;
        }

        this.removeChildren();

        const cover = this.createCover();
        this.addChild(cover);

        point = point || new paper.Point(0, 0);
        size = size || new paper.Size(0, 0);

        const roiArea = this.createROIArea(point, size);
        this.addChild(roiArea);
        this.roiArea = roiArea;

        const newPath = cover.exclude(this.roiArea, { insert: false });
        cover.replaceWith(newPath);
        this.cover = newPath;
        this.cover.opacity = this.isDefined() ? 0.5 : 0;

        this.roiArea.selected = this.editMode && size.width > 0 && size.height > 0;
        this.roiArea.fillColor = new paper.Color(`rgba(111, 207, 151, 0.01)`);
        this.roiArea.strokeWidth = 2;
        this.roiArea.strokeColor = new paper.Color(`rgb(111, 207, 151)`);
        this.roiArea.selectedColor = new paper.Color(`rgb(111, 207, 151)`);
        this.roiArea.strokeScaling = false;
    }

    private onMouseKeyReleased(): void {
        this.isChangeInProgress = false;
        this.setCursor(null);
    }

    private onMouseKeyPressed(e: paper.MouseEvent): void {
        this.lastObjectBoundsPoint = this.roiArea.bounds.point.clone();
        this.isChangeInProgress = true;
        this.setCursor(this.neuralaActivePiece);
    }

    private onMouseMoveOverLayer(e: paper.MouseEvent): void {
        if (!this.editMode) {
            this.setCursor(null);
            return;
        }

        if (!this.isChangeInProgress) {
            this.neuralaActivePiece = this.locate(e.point);
            this.setCursor(this.neuralaActivePiece);
        }

        this.onDrag(e);
    }

    private onDrag(e: paper.MouseEvent) {
        if (!this.isChangeInProgress || !this.editMode) {
            return;
        }

        switch (this.neuralaActivePiece) {
            case ACTIVE_OBJECTS.OBJECT:
                if (this.lastObjectBoundsPoint) {
                    moveObject(
                        e,
                        this.lastObjectBoundsPoint,
                        this.roiArea.bounds,
                        this.project.layers['assetLayer'],
                        this.project.layers['gridLayer']
                    );
                }
                break;
            case ACTIVE_OBJECTS.BOTTOM_RIGHT_CORNER:
                scaleSouthEast(
                    e,
                    this.roiArea.bounds,
                    this.project.layers['assetLayer'].bounds,
                    this.project.layers['assetLayer'].firstChild.scaling,
                    this.project.layers['gridLayer']
                );
                break;
            case ACTIVE_OBJECTS.BOTTOM_LEFT_CORNER:
                scaleSouthWest(
                    e,
                    this.roiArea.bounds,
                    this.project.layers['assetLayer'].bounds,
                    this.project.layers['assetLayer'].firstChild.scaling,
                    this.project.layers['gridLayer']
                );
                break;
            case ACTIVE_OBJECTS.TOP_RIGHT_CORNER:
                scaleNorthEast(
                    e,
                    this.roiArea.bounds,
                    this.project.layers['assetLayer'].bounds,
                    this.project.layers['assetLayer'].firstChild.scaling,
                    this.project.layers['gridLayer']
                );
                break;
            case ACTIVE_OBJECTS.TOP_LEFT_CORNER:
                scaleNorthWest(
                    e,
                    this.roiArea.bounds,
                    this.project.layers['assetLayer'].bounds,
                    this.project.layers['assetLayer'].firstChild.scaling,
                    this.project.layers['gridLayer']
                );
                break;
            case ACTIVE_OBJECTS.TOP_SEGMENT:
                scaleNorth(
                    e,
                    this.roiArea.bounds,
                    this.project.layers['assetLayer'].bounds,
                    this.project.layers['assetLayer'].firstChild.scaling,
                    this.project.layers['gridLayer']
                );
                break;
            case ACTIVE_OBJECTS.RIGHT_SEGMENT:
                scaleEast(
                    e,
                    this.roiArea.bounds,
                    this.project.layers['assetLayer'].bounds,
                    this.project.layers['assetLayer'].firstChild.scaling,
                    this.project.layers['gridLayer']
                );
                break;
            case ACTIVE_OBJECTS.BOTTOM_SEGMENT:
                scaleSouth(
                    e,
                    this.roiArea.bounds,
                    this.project.layers['assetLayer'].bounds,
                    this.project.layers['assetLayer'].firstChild.scaling,
                    this.project.layers['gridLayer']
                );
                break;
            case ACTIVE_OBJECTS.LEFT_SEGMENT:
                scaleWest(
                    e,
                    this.roiArea.bounds,
                    this.project.layers['assetLayer'].bounds,
                    this.project.layers['assetLayer'].firstChild.scaling,
                    this.project.layers['gridLayer']
                );
                break;
            default:
                break;
        }
        this.drawArea(this.roiArea.bounds.point, this.roiArea.bounds.size);
    }

    private setCursor(activeObject: ACTIVE_OBJECTS | null): void {
        // We should only show ROI cursors when editing the ROI.
        if (!this.editMode) {
            return;
        }

        switch (activeObject) {
            case ACTIVE_OBJECTS.BOTTOM_LEFT_CORNER:
                this.view.element.style.cursor = 'sw-resize';
                break;
            case ACTIVE_OBJECTS.BOTTOM_RIGHT_CORNER:
                this.view.element.style.cursor = 'se-resize';
                break;
            case ACTIVE_OBJECTS.TOP_LEFT_CORNER:
                this.view.element.style.cursor = 'nw-resize';
                break;
            case ACTIVE_OBJECTS.TOP_RIGHT_CORNER:
                this.view.element.style.cursor = 'ne-resize';
                break;
            case ACTIVE_OBJECTS.LEFT_SEGMENT:
                this.view.element.style.cursor = 'w-resize';
                break;
            case ACTIVE_OBJECTS.BOTTOM_SEGMENT:
                this.view.element.style.cursor = 's-resize';
                break;
            case ACTIVE_OBJECTS.TOP_SEGMENT:
                this.view.element.style.cursor = 'n-resize';
                break;
            case ACTIVE_OBJECTS.RIGHT_SEGMENT:
                this.view.element.style.cursor = 'e-resize';
                break;
            case ACTIVE_OBJECTS.OBJECT:
                if (this.isChangeInProgress) {
                    this.view.element.style.cursor = 'grabbing';
                } else {
                    this.view.element.style.cursor = 'grab';
                }
                break;
            default:
                this.view.element.style.cursor = 'default';
                break;
        }
    }

    private createROIArea(point: paper.Point, size: paper.Size) {
        const roiArea = new paper.Path.Rectangle(point, size);
        roiArea.strokeWidth = 4;
        roiArea.strokeColor = new paper.Color('black');
        roiArea.selectedColor = new paper.Color('rgb(111, 207, 151)');
        return roiArea;
    }

    private createCover(): paper.CompoundPath {
        const cover = new paper.CompoundPath({
            fillColor: new paper.Color('rgb(111, 207, 151)'),
            opacity: 0.5,
            strokeWidth: 0,
            selected: false,
            closed: true
        });

        const assetLayer = this.project.layers['assetLayer'];

        cover.addChild(
            new paper.Path.Rectangle(
                new paper.Point(assetLayer.bounds.x, assetLayer.bounds.y),
                new paper.Size(assetLayer.bounds.width, assetLayer.bounds.height)
            )
        );
        return cover;
    }

    private locate(point: paper.Point): ACTIVE_OBJECTS | null {
        const TOLERANCE = 3;
        if (this.roiArea.bounds.point.isClose(point, TOLERANCE)) {
            return ACTIVE_OBJECTS.TOP_LEFT_CORNER;
        }

        if (this.roiArea.bounds.topRight.isClose(point, TOLERANCE)) {
            return ACTIVE_OBJECTS.TOP_RIGHT_CORNER;
        }

        if (this.roiArea.bounds.bottomLeft.isClose(point, TOLERANCE)) {
            return ACTIVE_OBJECTS.BOTTOM_LEFT_CORNER;
        }

        if (this.roiArea.bounds.bottomRight.isClose(point, TOLERANCE)) {
            return ACTIVE_OBJECTS.BOTTOM_RIGHT_CORNER;
        }

        if (this.roiArea.bounds.bottomRight.isClose(point, TOLERANCE)) {
            return ACTIVE_OBJECTS.BOTTOM_RIGHT_CORNER;
        }

        // left edge
        if (Math.abs(this.roiArea.bounds.left - point.x) < TOLERANCE) {
            if (point.y >= this.roiArea.bounds.top && point.y <= this.roiArea.bounds.bottom) {
                return ACTIVE_OBJECTS.LEFT_SEGMENT;
            }
        }

        // right edge
        if (Math.abs(this.roiArea.bounds.right - point.x) < TOLERANCE) {
            if (point.y >= this.roiArea.bounds.top && point.y <= this.roiArea.bounds.bottom) {
                return ACTIVE_OBJECTS.RIGHT_SEGMENT;
            }
        }

        // TOP edge
        if (Math.abs(this.roiArea.bounds.top - point.y) < TOLERANCE) {
            if (point.x >= this.roiArea.bounds.left && point.x <= this.roiArea.bounds.right) {
                return ACTIVE_OBJECTS.TOP_SEGMENT;
            }
        }

        // bottom edge
        if (Math.abs(this.roiArea.bounds.bottom - point.y) < TOLERANCE) {
            if (point.x >= this.roiArea.bounds.left && point.x <= this.roiArea.bounds.right) {
                return ACTIVE_OBJECTS.BOTTOM_SEGMENT;
            }
        }

        if (this.roiArea.contains(point)) {
            return ACTIVE_OBJECTS.OBJECT;
        }

        return null;
    }

    public getTopLeftX(): number {
        return this.roiArea.bounds.topLeft.x;
    }

    public getTopLeftY(): number {
        return this.roiArea.bounds.topLeft.y;
    }

    public getBottomRightX(): number {
        return this.roiArea.bounds.bottomRight.x;
    }

    public getBottomRightY(): number {
        return this.roiArea.bounds.bottomRight.y;
    }

    public getWidth(): number {
        return this.roiArea.bounds.width;
    }

    public getHeight(): number {
        return this.roiArea.bounds.height;
    }

    public isChanging(): boolean {
        return this.isChangeInProgress;
    }
}



// WEBPACK FOOTER //
// ./src/components/workspace/objects/region-of-interest.ts